import { useMemo, useRef, useState } from "react";
import { LAYOUT, layoutZone, snapToGrid, type Stage, type Task, type Worklist } from "@board/shared";
import { archPath, rulePath, type Mark as MarkShape } from "./handDrawn";
import { dragThreshold, passedDragThreshold } from "./pointerGesture";
import { CompletedStack, Sticky, StickyDraft } from "./Sticky";
import { useBoard } from "./store";
import { ZoneResizeHandles } from "./ZoneResizeHandle";

const RULE_OFFSET = 40;
const DRAFT_ID = "__worklist-draft__";

/** A shared team list containing linked views of canonical client tasks. */
export function WorklistZone({
  worklist,
  tasks,
  scale,
  dropTarget,
  onTaskDragOver,
  onTaskDrop,
  onEdit,
  onExpandTask,
  completedExpanded,
  onToggleCompleted,
}: {
  worklist: Worklist;
  tasks: Task[];
  scale: number;
  dropTarget: { kind: "client" | "worklist"; id: string; stage: Stage } | null;
  onTaskDragOver: (taskId: string, clientX: number, clientY: number) => void;
  onTaskDrop: (taskId: string, clientX: number, clientY: number) => void;
  onEdit: () => void;
  onExpandTask: (taskId: string) => void;
  completedExpanded: boolean;
  onToggleCompleted: () => void;
}) {
  const updateWorklist = useBoard((state) => state.updateWorklist);
  const createTask = useBoard((state) => state.createTask);
  const deleteTask = useBoard((state) => state.deleteTask);
  const unlinkTaskFromWorklist = useBoard((state) => state.unlinkTaskFromWorklist);
  const tool = useBoard((state) => state.tool);
  const setTool = useBoard((state) => state.setTool);
  const isAdmin = useBoard((state) => state.isAdmin());
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [previewGeometry, setPreviewGeometry] = useState<{
    canvas_x: number; canvas_y: number; width: number; height: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [refused, setRefused] = useState(false);
  const [draft, setDraft] = useState<{ stage: Stage; since: string } | null>(null);
  const drag = useRef<{ pointer: number; x: number; y: number; ox: number; oy: number;
    moved: boolean; threshold: number; dx: number; dy: number } | null>(null);
  const completedTasks = useMemo(() => tasks.filter((task) => task.stage === "completed"), [tasks]);
  const completedCollapsed = completedTasks.length > 1 && !completedExpanded && draft?.stage !== "completed";
  const width = previewGeometry?.width ?? worklist.width;
  const layout = useMemo(() => layoutZone(width, draft ? [...tasks, {
    id: DRAFT_ID, stage: draft.stage, due_date: null, created_at: draft.since, priority: "normal",
  }] : tasks, undefined, { collapseCompleted: completedCollapsed }),
  [completedCollapsed, draft, tasks, width]);
  const height = Math.max(previewGeometry?.height ?? worklist.height, layout.requiredHeight);
  const canvasX = previewGeometry?.canvas_x ?? worklist.canvas_x;
  const canvasY = previewGeometry?.canvas_y ?? worklist.canvas_y;
  const placing = tool === "sticky";
  const draftCard = draft ? layout.cards.find((card) => card.taskId === DRAFT_ID) ?? null : null;
  const nameRule = useMemo(
    () => archPath(Math.max(worklist.name.length * 17 + 22, 120), `${worklist.id}:name`, { thickness: 6, rise: 5 }),
    [worklist.id, worklist.name],
  );
  const divider = useMemo(() => rulePath(height, `${worklist.id}:rule`), [height, worklist.id]);

  function begin(event: React.PointerEvent) {
    if (!isAdmin || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY,
      ox: worklist.canvas_x, oy: worklist.canvas_y, moved: false,
      threshold: dragThreshold(event.pointerType), dx: 0, dy: 0 };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function move(event: React.PointerEvent) {
    const active = drag.current;
    if (!active || active.pointer !== event.pointerId) return;
    if (!active.moved && !passedDragThreshold(
      active.x, active.y, event.clientX, event.clientY, active.threshold,
    )) return;
    active.moved = true;
    active.dx = (event.clientX - active.x) / scale;
    active.dy = (event.clientY - active.y) / scale;
    setDragging(true);
    setOffset({ x: active.dx, y: active.dy });
  }

  function finish(event: React.PointerEvent) {
    const active = drag.current;
    if (!active || active.pointer !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    setOffset({ x: 0, y: 0 });
    if (!active.moved) return;
    active.dx = (event.clientX - active.x) / scale;
    active.dy = (event.clientY - active.y) / scale;
    updateWorklist(worklist.id, {
      canvas_x: snapToGrid(active.ox + active.dx),
      canvas_y: snapToGrid(active.oy + active.dy),
    }).catch(() => {
      setRefused(true);
      window.setTimeout(() => setRefused(false), 2400);
    });
  }

  function cancel(event: React.PointerEvent) {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    setOffset({ x: 0, y: 0 });
  }

  function place(stage: Stage) {
    setDraft({ stage, since: new Date().toISOString() });
  }

  function commit(title: string) {
    const stage = draft?.stage;
    setDraft(null);
    if (!stage) return;
    setTool("select");
    createTask({ worklist_id: worklist.id, title, stage }).catch(() => {
      setRefused(true);
      window.setTimeout(() => setRefused(false), 2400);
    });
  }

  return (
    <section className={`zone worklist${dragging ? " zone--dragging" : ""}`}
      style={{ left: canvasX + offset.x, top: canvasY + offset.y,
        width, height }} aria-label={worklist.name}>
      <header className={`zone__header worklist__header${isAdmin ? " zone__header--draggable" : ""}`}
        style={{ height: LAYOUT.zone.headerHeight, padding: `0 ${LAYOUT.zone.padding}px` }}
        onPointerDown={begin} onPointerMove={move} onPointerUp={finish} onPointerCancel={cancel}>
        <span className="worklist__kind">WORKLIST</span>
        <h2 className="zone__name worklist__name">{worklist.name}</h2>
        {worklist.logo_updated_at ? <img className="zone__logo"
          src={`/api/worklists/${worklist.id}/logo?v=${encodeURIComponent(worklist.logo_updated_at)}`} alt="" /> : null}
        <span className="worklist__link" aria-hidden="true">↗</span>
        {isAdmin ? <button className="worklist__edit" aria-label={`Edit ${worklist.name}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.stopPropagation(); onEdit(); }}>✎</button> : null}
        {refused ? <span className="zone__refused">That move didn’t save</span> : null}
      </header>
      <Mark mark={nameRule} className="mark--name" left={LAYOUT.zone.padding} top={LAYOUT.zone.headerHeight - 8} />
      <div className={`zone__field${placing ? " zone__field--placing" : ""}`}>
        {layout.bands.map((band) => <div key={band.stage}
          className={`band band--${band.stage.replace("_", "-")}` +
            (placing ? " band--placing" : "") +
            (dropTarget?.kind === "worklist" && dropTarget.id === worklist.id && dropTarget.stage === band.stage ? " band--drop" : "")}
          style={{ left: band.x, top: band.y, width: band.width, height: band.height }}
          data-task-drop="worklist" data-drop-id={worklist.id} data-drop-stage={band.stage}
          onPointerDown={(event) => {
            if (event.button !== 0 || !placing) return;
            event.stopPropagation(); event.preventDefault(); place(band.stage);
          }}
          onMouseDown={(event) => { if (placing) event.preventDefault(); }}
          onDoubleClick={(event) => {
            if (placing || tool !== "select" || event.target !== event.currentTarget) return;
            place(band.stage);
          }} />)}
        {layout.cards.map((card) => {
          if (card.taskId === DRAFT_ID) return null;
          const task = tasks.find((candidate) => candidate.id === card.taskId);
          if (task && card.stage === "completed" && completedCollapsed) {
            return <CompletedStack key="completed-stack" task={task} at={card}
              count={completedTasks.length} onExpand={onToggleCompleted} />;
          }
          const linked = task?.worklist_id !== worklist.id;
          return task ? <Sticky key={`${task.id}:${worklist.id}`} task={task} at={card} scale={scale} linked={linked}
            onDragOver={(x, y) => onTaskDragOver(task.id, x, y)} onDrop={onTaskDrop}
            onExpand={onExpandTask} onRemoveLinked={linked
              ? () => unlinkTaskFromWorklist(task.id, worklist.id)
              : () => deleteTask(task.id)} /> : null;
        })}
        {draft && draftCard ? <StickyDraft stage={draft.stage} at={draftCard}
          onCommit={commit} onCancel={() => setDraft(null)} /> : null}
        {completedExpanded && completedTasks.length > 1 ? <button type="button"
          className="completed-stack__collapse"
          style={{ left: width - LAYOUT.zone.padding - 132,
            top: (layout.bands.find((band) => band.stage === "completed")?.y ?? 0) - 38 }}
          aria-expanded="true" onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.stopPropagation(); onToggleCompleted(); }}>
          Stack {completedTasks.length} completed
        </button> : null}
      </div>
      {layout.separators.map((y, index) => <Separator key={index} y={y}
        width={width - LAYOUT.zone.padding * 2} seed={`${worklist.id}:sep:${index}`} />)}
      {isAdmin ? <ZoneResizeHandles label={worklist.name}
        geometry={{ canvas_x: canvasX, canvas_y: canvasY, width, height }}
        minHeight={layout.requiredHeight} scale={scale}
        onPreview={setPreviewGeometry} onCommit={(next) => updateWorklist(worklist.id, next)}
        onProblem={() => {
          setRefused(true);
          window.setTimeout(() => setRefused(false), 2400);
        }} /> : null}
      <Mark mark={divider} className="mark--rule" left={width + RULE_OFFSET} top={0} />
    </section>
  );
}

function Separator({ y, width, seed }: { y: number; width: number; seed: string }) {
  const mark = useMemo(() => archPath(width, seed), [width, seed]);
  return <Mark mark={mark} className="mark--separator" left={LAYOUT.zone.padding} top={y} />;
}

function Mark({ mark, className, left, top }: { mark: MarkShape; className: string; left: number; top: number }) {
  return <svg className={`mark ${className}`} style={{ left: left + mark.x, top: top + mark.y }}
    width={mark.width} height={mark.height} viewBox={mark.viewBox} aria-hidden="true">
    <path d={mark.d} fill="var(--ink-marker)" />
  </svg>;
}
